import { ComponentProps, ComponentType } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslate } from 'i18n-calypso';

import feature, { FeatureFlag } from 'lib/utils/feature';
import { WPCloudPartnership } from 'data/signup/types';
import { TeamRole } from 'data/types';
import useHostPartnerQuery from 'data/signup/use-host-partner-query';
import { FallbackOutlet } from 'components/layout/fallback';
import Content from 'components/content';
import ContentHeading from 'components/content/heading';
import WPCloudPartnershipRedirectError from './error';

type PartnershipStatus = 'not_started' | 'incomplete' | 'complete';

function getPartnershipStatus( partnership?: WPCloudPartnership ): PartnershipStatus {
	// Team members added by an owner bypass the signup flow but still have an
	// approved partnership — treat them as complete without checking form fields.
	if ( partnership?.status === 'approved' ) {
		return 'complete';
	}

	if (
		partnership &&
		partnership.first_name &&
		partnership.last_name &&
		partnership.business_name &&
		partnership.url &&
		partnership.email &&
		partnership.last_date_accepted_tos
	) {
		if (
			partnership.last_date_accepted_pricing &&
			partnership.last_date_accepted_removal_subjection
		) {
			return 'complete';
		}

		return 'incomplete';
	}

	return 'not_started';
}

export function withWPCloudPartnership( requiredStatus: PartnershipStatus = 'complete' ) {
	return function WithWPCloudPartnership( Component: ComponentType ) {
		return ( props: ComponentProps<typeof Component> ) => {
			const { data, error, isSuccess, isLoading, isError } = useHostPartnerQuery();

			if ( isError ) {
				return <WPCloudPartnershipRedirectError status={ error.status } />;
			}

			if ( isLoading || ! isSuccess ) {
				return <FallbackOutlet />;
			}

			const status = getPartnershipStatus( data.host_partner?.partnerships?.wpcloud );

			if ( status === requiredStatus ) {
				return <Component { ...props } />;
			}

			switch ( status ) {
				case 'complete':
					return <Navigate to="/wpcloud" replace />;

				case 'incomplete':
					return <Navigate to="/wpcloud/signup/pricing" replace />;

				case 'not_started':
				default:
					return <Navigate to="/wpcloud/signup" replace />;
			}
		};
	};
}

export function withApprovedWPCloudPartnership( Component: ComponentType ) {
	return function WithApprovedWPCloudPartnership( props: ComponentProps<typeof Component> ) {
		const { data, error, isSuccess, isLoading, isError } = useHostPartnerQuery();

		if ( isError ) {
			return <WPCloudPartnershipRedirectError status={ error.status } />;
		}

		if ( isLoading || ! isSuccess ) {
			return <FallbackOutlet />;
		}

		if ( data.host_partner?.partnerships?.wpcloud?.status !== 'approved' ) {
			return <Navigate to="/wpcloud" replace />;
		}

		return <Component { ...props } />;
	};
}

export function withFeatureFlag( flag: FeatureFlag, redirect: string ) {
	return ( Component: ComponentType ) => {
		return function WithFeatureFlag( props: ComponentProps<typeof Component> ) {
			if ( ! feature( flag ) ) {
				return <Navigate to={ redirect } replace />;
			}

			return <Component { ...props } />;
		};
	};
}

export function withFeatureFlagsOrAutomattician( flags: FeatureFlag[], redirect: string ) {
	return ( Component: ComponentType ) => {
		return function WithFeatureFlagsOrAutomattician( props: ComponentProps<typeof Component> ) {
			const { data, error, isSuccess, isLoading, isError } = useHostPartnerQuery();

			if ( flags.every( feature ) ) {
				return <Component { ...props } />;
			}

			if ( isError ) {
				return <WPCloudPartnershipRedirectError status={ error.status } />;
			}

			if ( isLoading || ! isSuccess ) {
				return <FallbackOutlet />;
			}

			if ( data.is_automattician ) {
				return <Component { ...props } />;
			}

			return <Navigate to={ redirect } replace />;
		};
	};
}

/**
 * Renders a 403 Access Denied page. Used by withWPCloudRole when the user's
 * role does not permit access to the requested route.
 */
export function WPCloudAccessDenied() {
	const translate = useTranslate();
	return (
		<Content>
			<ContentHeading
				section={ translate( 'Error 403' ) }
				title={ translate( 'Access denied.' ) }
				description={ translate(
					'You don\u2019t have permission to view this page. Contact your account owner if you believe this is a mistake.',
				) }
			/>
		</Content>
	);
}

/**
 * Route guard HOC that checks the current user's WP Cloud partner role.
 *
 * Renders a 403 component (not a redirect) if the user's role is not in
 * allowedRoles. The sidebar hides inaccessible nav items so users encounter
 * the 403 only via direct URL.
 *
 * Usage (in routes.tsx):
 *   pipe( withApprovedWPCloudPartnership, withWPCloudRole( ['owner'] ), withTitle( '…' ) )( Component )
 */
export function withWPCloudRole( allowedRoles: TeamRole[] ) {
	return ( Component: ComponentType ) => {
		return function WithWPCloudRole( props: ComponentProps<typeof Component> ) {
			const { data, error, isSuccess, isLoading, isError } = useHostPartnerQuery();

			if ( isError ) {
				return <WPCloudPartnershipRedirectError status={ error.status } />;
			}

			if ( isLoading || ! isSuccess ) {
				return <FallbackOutlet />;
			}

			// Automatticians get view-only access to all portal pages — role check bypassed.
			if ( data.is_automattician ) {
				return <Component { ...props } />;
			}

			// Owners who have no membership record (pre-backfill) are identified
			// via the direct host_partner blob path and return 'owner' as their role.
			const role = data.current_user_role ?? 'owner';

			if ( ! allowedRoles.includes( role ) ) {
				return <WPCloudAccessDenied />;
			}

			return <Component { ...props } />;
		};
	};
}
